// Eine Kachel vom Startbildschirm in die untere Leiste ziehen.
//
// Kachelfeld und Leiste sind GESCHWISTER (beide hängen in CrmShell) — die eine
// weiß nichts von der anderen. Für das Ziehen müssen sie es aber: Die Leiste
// muss aufleuchten, wenn der Finger über ihr schwebt, und das Kachelfeld muss
// wissen, wann losgelassen wurde. Genau dafür ist dieser Kontext da.
//
// Bewusst KEINE HTML5-Drag-and-Drop-API: Die gibt es auf Touch schlicht nicht.
// Pointer-Events funktionieren auf Finger, Maus und Stift gleichermaßen.

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

interface ZiehZustand {
  /** Welches Modul hängt gerade am Finger? NULL = niemand zieht. */
  modulId: string | null;
  /** Schwebt der Finger über der unteren Leiste? */
  ueberLeiste: boolean;
  /** Wo ist der Finger gerade (für das Geisterbild). */
  x: number;
  y: number;
}

interface PinDragApi extends ZiehZustand {
  beginnen: (modulId: string, x: number, y: number) => void;
  bewegen: (x: number, y: number) => void;
  /** Loslassen. Gibt zurück, ob über der Leiste losgelassen wurde. */
  beenden: () => boolean;
  abbrechen: () => void;
}

const Ctx = createContext<PinDragApi | null>(null);

/** Die Leiste meldet sich hier an, damit wir ihre Fläche kennen. */
export const LEISTE_MARKE = "bottom-bar";

function ueberDerLeiste(x: number, y: number) {
  const el = document.querySelector(`[data-testid="${LEISTE_MARKE}"]`);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  // Etwas Toleranz nach oben: Wer die Leiste treffen will, zielt selten exakt.
  // 24 px kosten nichts — darüber liegt ohnehin nur der Seiteninhalt.
  return x >= r.left && x <= r.right && y >= r.top - 24 && y <= r.bottom;
}

export function PinDragProvider({ children }: { children: ReactNode }) {
  const [z, setZ] = useState<ZiehZustand>({ modulId: null, ueberLeiste: false, x: 0, y: 0 });
  // Der letzte Stand auch als Ref: `beenden()` wird aus einem Event-Handler
  // gerufen und würde sonst den Zustand von vor dem letzten Rendern lesen.
  const jetzt = useRef(z);
  jetzt.current = z;

  const beginnen = useCallback((modulId: string, x: number, y: number) => {
    setZ({ modulId, ueberLeiste: ueberDerLeiste(x, y), x, y });
  }, []);

  const bewegen = useCallback((x: number, y: number) => {
    setZ((v) => (v.modulId ? { ...v, x, y, ueberLeiste: ueberDerLeiste(x, y) } : v));
  }, []);

  const beenden = useCallback(() => {
    const traf = jetzt.current.modulId ? jetzt.current.ueberLeiste : false;
    setZ({ modulId: null, ueberLeiste: false, x: 0, y: 0 });
    return traf;
  }, []);

  const abbrechen = useCallback(() => {
    setZ({ modulId: null, ueberLeiste: false, x: 0, y: 0 });
  }, []);

  const api = useMemo(
    () => ({ ...z, beginnen, bewegen, beenden, abbrechen }),
    [z, beginnen, bewegen, beenden, abbrechen]
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function usePinDrag() {
  const v = useContext(Ctx);
  if (!v) throw new Error("usePinDrag braucht den PinDragProvider");
  return v;
}
